import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, RadixTable, TColumn } from '@features/ui';
import Pagination from '@features/ui/components/pagination.tsx';
import { EmailQuery } from '@src/types';
import { getUsers } from '@src/services/settings.ts';
import { useAuthStore } from '@src/stores/authStore';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';

interface UserTableProps {
  onEdit?: (user: any) => void;
}
export const UserTable: FC<UserTableProps> = ({ onEdit }) => {
  const { auth } = useAuthStore();
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [users, setUsers] = useState<any[]>([]);

  const fetchUsers = useCallback(async (params?: EmailQuery) => {
    const res = await getUsers(params);

    setTotalPage(res.data.total_pages);
    setUsers(res.data.data);
  }, []);

  useEffect(() => {
    fetchUsers().then();
  }, [fetchUsers]);

  const handleChangePage = useCallback(
    async (page: number) => {
      setPage(page);
      await fetchUsers({ page });
    },
    [fetchUsers]
  );

  const columns = useMemo((): TColumn<any>[] => {
    const baseColumns: TColumn<any>[] = [
      {
        title: '#',
        render: (_item, index) => <p>{(index ?? 0) + 1}</p>,
      },
      {
        title: 'User Name',
        render: (item) => <p>{item?.username ?? ''}</p>,
      },
      { title: 'Email', render: (item) => <p>{item?.email}</p> },
      {
        title: 'Status',
        render: (item) => (
          <p className={item?.account_active ? 'text-lime' : 'text-[#C53737]'}>
            {item?.account_active ? 'Active' : 'Inactive'}
          </p>
        ),
      },
    ];

    if (!canAccess(UserRolePermission.MANAGE_USERS, auth?.user?.permissions)) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        title: '',
        render: (item) => (
          <Button
            size={'sm'}
            variant={'secondary'}
            onClick={() => item?.id && onEdit?.(item)}
          >
            Edit
          </Button>
        ),
      },
    ];
  }, [onEdit, auth?.user?.permissions]);

  return (
    <Card className={'flex flex-col items-end'}>
      <RadixTable columns={columns} data={users} />
      <Pagination
        current={page}
        total={totalPage}
        onChange={handleChangePage}
      />
    </Card>
  );
};
